import React, { useState, useEffect, useCallback } from 'react';
import {
    StickyNote, Plus, Search, Pin, Archive, Tag, Hash, Filter, LayoutGrid, List,
    Loader2, AlertTriangle, X, ChevronDown, Calendar, Eye, EyeOff, Lock,
    Globe, Users, MoreHorizontal, RefreshCw, Briefcase
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    fetchNotes, fetchNoteTags, fetchNoteStats, createNote, updateNote,
    deleteNote, toggleNotePin, toggleNoteArchive, fetchNoteById
} from '../api/notes';
import NoteEditor from './NoteEditor';
import NoteCard from './NoteCard';
import TemplatePicker from './TemplatePicker';

const TABS = [
    { id: 'all', label: 'All Notes', icon: StickyNote },
    { id: 'linked', label: 'Linked', icon: Briefcase },
    { id: 'pinned', label: 'Pinned', icon: Pin },
    { id: 'tags', label: 'Tags', icon: Hash },
    { id: 'archived', label: 'Archived', icon: Archive }
];

const VISIBILITY_OPTIONS = [
    { value: 'PRIVATE', label: 'Private', icon: Lock, description: 'Only you can see' },
    { value: 'PROTECTED', label: 'Team', icon: Users, description: 'Team members can see' },
    { value: 'PUBLIC', label: 'Public', icon: Globe, description: 'Anyone can see' }
];

const NotesPage = ({ currentUserEmail, onNavigateToCase, cases = [] }) => {
    // State
    const [notes, setNotes] = useState([]);
    const [tags, setTags] = useState([]);
    const [stats, setStats] = useState({ total: 0, pinned: 0, archived: 0, linked: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // UI State
    const [activeTab, setActiveTab] = useState('all');
    const [viewMode, setViewMode] = useState('list'); // 'list' | 'grid'
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTag, setSelectedTag] = useState(null);
    const [visibilityFilter, setVisibilityFilter] = useState(null);
    const [showFilters, setShowFilters] = useState(false);

    // Editor State
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isTemplatePickerOpen, setIsTemplatePickerOpen] = useState(false);

    // Check for noteId in URL and open that note
    useEffect(() => {
        const checkUrlForNote = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const noteId = urlParams.get('noteId');

            if (noteId) {
                try {
                    const res = await fetchNoteById(noteId);
                    if (res.data) {
                        setEditingNote(res.data);
                        setIsEditorOpen(true);
                        // Clean up URL without reload
                        const newUrl = window.location.pathname;
                        window.history.replaceState({}, '', newUrl);
                    }
                } catch (err) {
                    console.error('Failed to load note from URL:', err);
                    setError('The linked note could not be found or you do not have access to it.');
                }
            }
        };

        checkUrlForNote();
    }, []);

    // Load notes data
    const loadNotes = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {};

            if (activeTab === 'pinned') {
                params.pinned = true;
            } else if (activeTab === 'archived') {
                params.archived = true;
            } else if (activeTab === 'linked') {
                params.filter = 'linked';
                params.linked = true;
            } else if (activeTab === 'all') {
                params.archived = false;
            }

            if (selectedTag) {
                params.tag = selectedTag;
            }

            if (visibilityFilter) {
                params.visibility = visibilityFilter;
            }

            if (searchTerm) {
                params.search = searchTerm;
            }

            const [notesRes, tagsRes, statsRes] = await Promise.all([
                fetchNotes(params),
                fetchNoteTags().catch(() => ({ data: [] })),
                fetchNoteStats().catch(() => ({ data: { total: 0, pinned: 0, archived: 0, linked: 0 } }))
            ]);

            const fetchedNotes = notesRes.data || [];
            const linkedNotes = activeTab === 'linked'
                ? fetchedNotes.filter(note => note.case_id || note.caseId)
                : fetchedNotes;
            setNotes(linkedNotes);
            setTags(tagsRes.data || []);
            setStats(statsRes.data || { total: 0, pinned: 0, archived: 0, linked: 0 });
        } catch (err) {
            console.error('Failed to load notes:', err);
            setError(err.message || 'Failed to load notes');
            setNotes([]);
        } finally {
            setLoading(false);
        }
    }, [activeTab, selectedTag, visibilityFilter, searchTerm]);

    useEffect(() => {
        loadNotes();
    }, [loadNotes]);

    // Filter notes by search term (client-side for instant feedback)
    const filteredNotes = notes.filter(note => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            note.content?.toLowerCase().includes(term) ||
            note.tags?.some(t => t.toLowerCase().includes(term))
        );
    });

    // Group notes: pinned first, then by date
    const sortedNotes = [...filteredNotes].sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt);
    });

    // Handlers
    const handleCreateNote = () => {
        setEditingNote(null);
        setIsTemplatePickerOpen(true);
    };

    const handleTemplateSelect = (content) => {
        setEditingNote({ content });
        setIsTemplatePickerOpen(false);
        setIsEditorOpen(true);
    };

    const handleEditNote = (note) => {
        setEditingNote(note);
        setIsEditorOpen(true);
    };

    const handleSaveNote = async (noteData, options = {}) => {
        const { closeAfterSave = true } = options;
        setIsSaving(true);
        try {
            let savedNote;
            if (editingNote && editingNote.id) {
                const res = await updateNote(editingNote.id, noteData);
                savedNote = res.data;
                setNotes(prev => prev.map(n => n.id === editingNote.id ? savedNote : n));
            } else {
                const res = await createNote(noteData);
                savedNote = res.data;
                setNotes(prev => [savedNote, ...prev]);
                // Update editingNote with the new ID so subsequent autosaves update instead of create
                setEditingNote(savedNote);
            }

            if (closeAfterSave) {
                setIsEditorOpen(false);
                setEditingNote(null);
            }

            // Refresh stats
            const statsRes = await fetchNoteStats().catch(() => ({ data: stats }));
            setStats(statsRes.data);

            return savedNote;
        } catch (err) {
            console.error('Failed to save note:', err);
            throw err;
        } finally {
            setIsSaving(false);
        }
    };

    // Autosave handler - saves without closing the editor
    const handleAutosaveNote = async (noteData) => {
        return handleSaveNote(noteData, { closeAfterSave: false });
    };

    const handleDeleteNote = async (noteId) => {
        try {
            await deleteNote(noteId);
            setNotes(prev => prev.filter(n => n.id !== noteId));
            // Refresh stats
            const statsRes = await fetchNoteStats().catch(() => ({ data: stats }));
            setStats(statsRes.data);
        } catch (err) {
            console.error('Failed to delete note:', err);
            alert('Failed to delete note: ' + err.message);
        }
    };

    const handleTogglePin = async (noteId, currentPinned) => {
        try {
            const res = await toggleNotePin(noteId, !currentPinned);
            setNotes(prev => prev.map(n => n.id === noteId ? res.data : n));
            // Refresh stats
            const statsRes = await fetchNoteStats().catch(() => ({ data: stats }));
            setStats(statsRes.data);
        } catch (err) {
            console.error('Failed to toggle pin:', err);
        }
    };

    const handleToggleArchive = async (noteId, currentArchived) => {
        try {
            const res = await toggleNoteArchive(noteId, !currentArchived);
            if (activeTab !== 'archived') {
                // Remove from current view if archiving
                setNotes(prev => prev.filter(n => n.id !== noteId));
            } else {
                // Update in place if in archived view
                setNotes(prev => prev.map(n => n.id === noteId ? res.data : n));
            }
            // Refresh stats
            const statsRes = await fetchNoteStats().catch(() => ({ data: stats }));
            setStats(statsRes.data);
        } catch (err) {
            console.error('Failed to toggle archive:', err);
        }
    };

    const handleTagClick = (tag) => {
        setSelectedTag(tag);
        setActiveTab('tags');
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedTag(null);
        setVisibilityFilter(null);
    };

    // Extract tags from content (for display)
    const extractTags = (content) => {
        if (!content) return [];
        const tagPattern = /#[\w-]+/g;
        const matches = content.match(tagPattern) || [];
        return [...new Set(matches.map(t => t.substring(1)))];
    };

    return (
        <div className="flex flex-col h-full bg-[#f7f4ee] dark:bg-slate-950">
            {/* Hero */}
            <div className="relative overflow-hidden px-6 py-10 sm:py-12">
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(120deg, rgba(245, 158, 11, 0.95), rgba(15, 23, 42, 0.95))'
                    }}
                />
                <div
                    className="absolute inset-0 opacity-70"
                    style={{
                        backgroundImage: 'radial-gradient(circle at 15% 15%, rgba(255,255,255,0.3), transparent 40%), radial-gradient(circle at 80% 10%, rgba(255,255,255,0.2), transparent 35%)'
                    }}
                />
                <div className="relative max-w-5xl mx-auto">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                        <div className="text-white">
                            <p className="text-sm uppercase tracking-[0.2em] text-white/70">Notes workspace</p>
                            <h2 className="text-3xl sm:text-4xl font-semibold mt-2">A workspace for every idea</h2>
                            <p className="text-sm sm:text-base text-white/80 mt-2 max-w-xl">
                                Collect meeting notes, tag key insights, and keep everyone aligned.
                            </p>
                        </div>
                        <div className="w-full lg:w-[360px]">
                            <label className="text-xs font-semibold text-white/80 uppercase tracking-[0.2em]">
                                Search notes
                            </label>
                            <div className="relative mt-3">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search all notes..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-12 pr-4 py-3 bg-white/95 dark:bg-slate-800 border-0 rounded-xl shadow-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-amber-200"
                                />
                            </div>
                            <div className="flex flex-wrap gap-2 mt-4 text-xs text-white/80">
                                <span className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-medium">
                                    {stats.total} notes
                                </span>
                                <span className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-medium">
                                    {stats.pinned} pinned
                                </span>
                            </div>
                            <button
                                onClick={handleCreateNote}
                                className="mt-4 w-full px-4 py-2.5 bg-white text-slate-900 hover:bg-white/90 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                            >
                                <Plus className="w-5 h-5" />
                                Create Note
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-auto px-6 pb-6 -mt-8">
                {/* Header */}
                <div className="px-6 py-6 border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900/80 backdrop-blur rounded-2xl shadow-xl mb-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-xl font-bold dark:text-white text-slate-900">Notes Workspace</h2>
                            <p className="dark:text-slate-400 text-slate-500 text-sm mt-1">
                                Your central hub for all notes and ideas.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                        <button
                            onClick={handleCreateNote}
                            className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
                        >
                            <Plus size={18} />
                            New Note
                        </button>
                        {/* View Toggle */}
                        <div className="flex dark:bg-slate-800 bg-slate-100 p-1 rounded-lg border dark:border-slate-700 border-slate-200">
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded-md transition-all duration-200 ${viewMode === 'list'
                                    ? 'dark:bg-slate-600 bg-white dark:text-white text-amber-600 shadow-md'
                                    : 'text-slate-400 hover:text-amber-600 dark:hover:text-white hover:bg-white dark:hover:bg-slate-700/50'
                                    }`}
                                title="List View"
                            >
                                <List size={18} />
                            </button>
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded-md transition-all duration-200 ${viewMode === 'grid'
                                    ? 'dark:bg-slate-600 bg-white dark:text-white text-amber-600 shadow-md'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700/50'
                                    }`}
                                title="Grid View"
                            >
                                <LayoutGrid size={18} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    {[
                        { label: 'Total Notes', count: stats.total, color: 'amber', onClick: () => setActiveTab('all') },
                        { label: 'Linked', count: stats.linked, color: 'blue', onClick: () => setActiveTab('linked') },
                        { label: 'Pinned', count: stats.pinned, color: 'sky', onClick: () => setActiveTab('pinned') },
                        { label: 'Tags', count: tags.length, color: 'purple', onClick: () => setActiveTab('tags') },
                        { label: 'Archived', count: stats.archived, color: 'slate', onClick: () => setActiveTab('archived') }
                    ].map((stat, i) => (
                        <motion.div
                            key={stat.label}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 }}
                            onClick={stat.onClick}
                            className={`p-4 rounded-xl border cursor-pointer transition-all group dark:bg-slate-900/50 bg-white dark:border-slate-800 border-slate-200 hover:border-${stat.color}-500/50 dark:hover:bg-slate-800/50 hover:bg-slate-50`}
                        >
                            <p className="text-xs font-bold uppercase tracking-wider dark:text-slate-400 text-slate-500">{stat.label}</p>
                            <h3 className={`text-3xl font-bold mt-2 text-${stat.color}-500 group-hover:scale-105 transition-transform origin-left`}>
                                {loading ? '-' : stat.count}
                            </h3>
                        </motion.div>
                    ))}
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-2 border-b dark:border-slate-800 border-slate-200 -mx-8 px-8">
                    {TABS.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => {
                                setActiveTab(tab.id);
                                if (tab.id !== 'tags') setSelectedTag(null);
                            }}
                            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-all border-b-2 -mb-px ${activeTab === tab.id
                                ? 'dark:text-amber-400 text-amber-600 border-amber-500'
                                : 'dark:text-slate-400 text-slate-500 border-transparent hover:text-slate-900 dark:hover:text-white hover:border-slate-300 dark:hover:border-slate-600'
                                }`}
                        >
                            <tab.icon size={16} />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Search & Filters Bar */}
            <div className="px-8 py-4 border-b dark:border-slate-800 border-slate-200 dark:bg-slate-900/30 bg-white/50 flex items-center gap-4">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                        type="text"
                        placeholder="Search notes..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 rounded-lg dark:text-white text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>

                {/* Tag Filter (visible when in tags tab) */}
                {activeTab === 'tags' && tags.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm dark:text-slate-400 text-slate-500">Filter by tag:</span>
                        <div className="flex gap-1 flex-wrap">
                            {tags.slice(0, 10).map(tag => (
                                <button
                                    key={tag}
                                    onClick={() => setSelectedTag(selectedTag === tag ? null : tag)}
                                    className={`px-3 py-1 text-xs rounded-full transition-all font-semibold ${selectedTag === tag
                                        ? 'bg-amber-500 text-white'
                                        : 'dark:bg-slate-800 bg-slate-100 dark:text-slate-300 text-slate-600 hover:bg-amber-500/20 hover:text-amber-600 dark:hover:text-amber-400'
                                        }`}
                                >
                                    #{tag}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Active Filters */}
                {(selectedTag || visibilityFilter || searchTerm) && (
                    <button
                        onClick={clearFilters}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs dark:bg-red-500/10 bg-red-50 text-red-500 rounded-lg hover:bg-red-500/20 transition"
                    >
                        <X size={14} />
                        Clear filters
                    </button>
                )}
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-8">
                {loading ? (
                    <div className="flex flex-col items-center justify-center h-64">
                        <Loader2 size={32} className="text-amber-500 animate-spin mb-3" />
                        <p className="text-sm dark:text-slate-400 text-slate-500">Loading notes...</p>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center h-64">
                        <div className="p-4 rounded-full bg-red-500/10 mb-4">
                            <AlertTriangle size={32} className="text-red-500" />
                        </div>
                        <p className="text-sm text-red-500 mb-4">{error}</p>
                        <button
                            onClick={loadNotes}
                            className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg text-sm transition"
                        >
                            Try Again
                        </button>
                    </div>
                ) : sortedNotes.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64">
                        <div className="p-6 rounded-2xl dark:bg-slate-800/50 bg-slate-100 mb-4">
                            <StickyNote size={48} className="dark:text-slate-600 text-slate-400" />
                        </div>
                        <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
                            {activeTab === 'archived' ? 'No archived notes' :
                                activeTab === 'pinned' ? 'No pinned notes' :
                                    searchTerm ? 'No matching notes' : 'No notes yet'}
                        </h3>
                        <p className="text-sm dark:text-slate-400 text-slate-500 mb-6 text-center max-w-md">
                            {activeTab === 'archived' ? 'Archived notes will appear here.' :
                                activeTab === 'pinned' ? 'Pin important notes to access them quickly.' :
                                    searchTerm ? 'Try a different search term.' :
                                        'Create your first note to capture ideas, track insights, and stay organized.'}
                        </p>
                        {!searchTerm && activeTab !== 'archived' && (
                            <button
                                onClick={handleCreateNote}
                                className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg font-medium flex items-center gap-2 transition-all hover:scale-105"
                            >
                                <Plus size={18} />
                                Create Note
                            </button>
                        )}
                    </div>
                ) : (
                    <AnimatePresence mode="popLayout">
                        <div className={viewMode === 'grid'
                            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'
                            : 'flex flex-col gap-3'
                        }>
                            {sortedNotes.map((note, index) => (
                                <motion.div
                                    key={note.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    transition={{ delay: index * 0.03 }}
                                >
                                    <NoteCard
                                        note={note}
                                        viewMode={viewMode}
                                        onEdit={() => handleEditNote(note)}
                                        onDelete={() => handleDeleteNote(note.id)}
                                        onTogglePin={() => handleTogglePin(note.id, note.pinned)}
                                        onToggleArchive={() => handleToggleArchive(note.id, note.archived)}
                                        onTagClick={handleTagClick}
                                        currentUserEmail={currentUserEmail}
                                        onNavigateToCase={onNavigateToCase}
                                        cases={cases}
                                    />
                                </motion.div>
                            ))}
                        </div>
                    </AnimatePresence>
                )}
            </div>

            </div>

            {/* Template Picker Modal */}
            <AnimatePresence>
                {isTemplatePickerOpen && (
                    <TemplatePicker
                        onSelect={handleTemplateSelect}
                        onClose={() => setIsTemplatePickerOpen(false)}
                    />
                )}
            </AnimatePresence>

            {/* Note Editor Modal */}
            <AnimatePresence>
                {isEditorOpen && (
                    <NoteEditor
                        note={editingNote}
                        onSave={handleSaveNote}
                        onAutosave={handleAutosaveNote}
                        onClose={() => {
                            setIsEditorOpen(false);
                            setEditingNote(null);
                        }}
                        isSaving={isSaving}
                        cases={cases}
                        onNavigateToCase={onNavigateToCase}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default NotesPage;
